import { useState } from 'react';
import { updateGame } from '../../services/content/gameService';
import type { Game, TimelineStage } from '../../types/content/game';

type View = 'list' | 'detail';

interface GameTimelineModalProps {
  game: Game;
  onClose: () => void;
  onSuccess: () => void;
}

export function GameTimelineModal({ game, onClose, onSuccess }: GameTimelineModalProps) {
  const [stages, setStages] = useState<TimelineStage[]>(
    game.timelineStages ? JSON.parse(JSON.stringify(game.timelineStages)) : []
  );
  const [view, setView] = useState<View>('list');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [stageName, setStageName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSmall = 'text-xs px-2 py-1 rounded transition-colors';

  function openForm(index: number | null) {
    setStageName(index !== null ? stages[index].stage_name : '');
    setEditingIndex(index);
    setView('detail');
  }

  function saveItem() {
    if (!stageName.trim()) return;
    const updated = [...stages];
    if (editingIndex !== null) {
      updated[editingIndex] = { ...updated[editingIndex], stage_name: stageName.trim() };
    } else {
      updated.push({ stage_number: updated.length + 1, stage_name: stageName.trim() });
    }
    setStages(updated.map((s, i) => ({ ...s, stage_number: i + 1 })));
    setView('list');
  }

  function deleteItem(index: number) {
    setStages(stages.filter((_, i) => i !== index).map((s, i) => ({ ...s, stage_number: i + 1 })));
  }

  function moveItem(index: number, dir: -1 | 1) {
    const ni = index + dir;
    if (ni < 0 || ni >= stages.length) return;
    const updated = [...stages];
    [updated[index], updated[ni]] = [updated[ni], updated[index]];
    setStages(updated.map((s, i) => ({ ...s, stage_number: i + 1 })));
  }

  async function handleSave() {
    setError('');
    setLoading(true);
    try {
      await updateGame(game.id, { ...game, timelineStages: stages });
      onSuccess();
    } catch {
      setError('Failed to update timeline stages');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {view === 'list' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Timeline Stages</h2>
              <p className="text-sm text-gray-500 mb-4">{stages.length} stage(s)</p>
              <div className="flex justify-end mb-2">
                <button type="button" onClick={() => openForm(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add Stage</button>
              </div>
              {stages.length === 0 ? (
                <p className="text-sm text-gray-400">No stages yet</p>
              ) : (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {stages.map((s, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-5 text-center">{s.stage_number}</span>
                        <span className="text-sm text-gray-700">{s.stage_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => moveItem(i, -1)} disabled={i === 0} className={`${btnSmall} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}>&#9650;</button>
                        <button type="button" onClick={() => moveItem(i, 1)} disabled={i === stages.length - 1} className={`${btnSmall} text-gray-400 hover:bg-gray-100 disabled:opacity-30`}>&#9660;</button>
                        <button type="button" onClick={() => openForm(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                        <button type="button" onClick={() => deleteItem(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
              <button type="button" onClick={handleSave} disabled={loading} className={btnPrimary}>{loading ? 'Saving...' : 'Save'}</button>
            </div>
          </>
        )}
        {view === 'detail' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{editingIndex !== null ? 'Edit Stage' : 'Add Stage'}</h2>
              <div>
                <label className={labelClass}>Stage Name *</label>
                <input type="text" value={stageName} onChange={(e) => setStageName(e.target.value)} className={inputClass} placeholder="e.g. Introduction" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('list')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveItem} disabled={!stageName.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
